import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/context/LanguageContext';

// redeploy-touch: 2026-06-16 v2 (force frontend deploy to pick up Activity Log nav)

interface NavItem {
  to:         string;
  icon:       string;
  labelKey:   string;
  adminOnly?: boolean;
}

interface NavGroup {
  labelKey:   string;
  icon:       string;
  items:      NavItem[];
}

type NavEntry = NavItem | NavGroup;

const isGroup = (e: NavEntry): e is NavGroup => 'items' in e;

const OVERVIEW: NavGroup = {
  labelKey: 'nav.overview',
  icon:  '⊞',
  items: [
    { to: '/',            icon: '⊞',  labelKey: 'nav.dashboard'  },
    { to: '/charts',      icon: '📊', labelKey: 'nav.analytics'  },
    { to: '/anbiyam-map', icon: '🗺', labelKey: 'nav.zoneMap'    },
  ],
};

const CEMETERY: NavGroup = {
  labelKey: 'nav.cemetery',
  icon:  '✞',
  items: [
    { to: '/cemetery',            icon: '✞', labelKey: 'nav.parish'    },
    { to: '/non-parish-cemetery', icon: '✦', labelKey: 'nav.nonParish' },
  ],
};

const USER_MANAGEMENT: NavGroup = {
  labelKey: 'nav.userMgmt',
  icon:  '👤',
  items: [
    { to: '/users',         icon: '👤', labelKey: 'nav.users',       adminOnly: true },
    { to: '/activity-log',  icon: '📋', labelKey: 'nav.activityLog', adminOnly: true },
  ],
};

const NAV: NavEntry[] = [
  OVERVIEW,
  { to: '/anbiyam',       icon: '⛪', labelKey: 'nav.anbiyam'      },
  { to: '/families',      icon: '👨‍👩‍👧', labelKey: 'nav.families'     },
  { to: '/subscriptions', icon: '₹',  labelKey: 'nav.subscriptions' },
  { to: '/subscriptions/bulk-export', icon: '📦', labelKey: 'nav.bulkExport', adminOnly: true },
  { to: '/subscriptions/collection-review', icon: '🖨', labelKey: 'nav.collectionReview', adminOnly: true },
  CEMETERY,
  { to: '/certificates',  icon: '📜', labelKey: 'nav.certificates' },
  USER_MANAGEMENT,
];

interface Props {
  onClose?: () => void;
}

export default function Sidebar({ onClose }: Props) {
  const { isAdmin } = useAuth();
  const { pathname } = useLocation();
  const { t } = useLanguage();

  // Auto-expand whichever group contains the active route; user can still toggle.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const entry of NAV) {
      if (isGroup(entry)) {
        initial[entry.labelKey] = entry.items.some((i) => (i.to === '/' ? pathname === '/' : pathname.startsWith(i.to)));
      }
    }
    return initial;
  });

  const toggleGroup = (labelKey: string) =>
    setOpenGroups((g) => ({ ...g, [labelKey]: !g[labelKey] }));

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-5 py-3 text-sm font-georgia transition-colors
     ${isActive
       ? 'bg-teal text-white'
       : 'text-blue-200 hover:bg-navy-light hover:text-gold'}`;

  return (
    <aside className="w-56 h-full bg-navy flex flex-col overflow-y-auto">
      {/* Close button — visible only on mobile */}
      {onClose && (
        <div className="px-5 py-3 border-b border-navy-light flex justify-end md:hidden">
          <button
            onClick={onClose}
            className="text-blue-300 hover:text-gold text-lg leading-none"
            aria-label="Close menu"
          >
            ✕
          </button>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 py-4">
        {NAV.map((entry) => {
          if (isGroup(entry)) {
            const visibleItems = entry.items.filter((i) => !i.adminOnly || isAdmin);
            if (visibleItems.length === 0) return null;
            const open = openGroups[entry.labelKey] ?? false;
            return (
              <div key={entry.labelKey}>
                <button
                  type="button"
                  onClick={() => toggleGroup(entry.labelKey)}
                  className="w-full flex items-center gap-3 px-5 py-3 text-sm font-georgia text-blue-200 hover:bg-navy-light hover:text-gold transition-colors"
                >
                  <span className="w-5 text-center">{entry.icon}</span>
                  <span className="flex-1 text-left">{t(entry.labelKey)}</span>
                  <span className={`text-xs transition-transform ${open ? 'rotate-90' : ''}`}>›</span>
                </button>
                {open && (
                  <div className="bg-navy-light/30">
                    {visibleItems.map(({ to, icon, labelKey }) => (
                      <NavLink
                        key={to}
                        to={to}
                        end={to === '/'}
                        onClick={onClose}
                        className={({ isActive }) =>
                          `flex items-center gap-3 pl-10 pr-5 py-2.5 text-sm font-georgia transition-colors
                           ${isActive
                             ? 'bg-teal text-white'
                             : 'text-blue-200 hover:bg-navy-light hover:text-gold'}`
                        }
                      >
                        <span className="w-5 text-center">{icon}</span>
                        {t(labelKey)}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          if (entry.adminOnly && !isAdmin) return null;
          return (
            <NavLink
              key={entry.to}
              to={entry.to}
              end={entry.to === '/'}
              onClick={onClose}
              className={linkClass}
            >
              <span className="w-5 text-center">{entry.icon}</span>
              {t(entry.labelKey)}
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
}
